import { useEffect, useMemo, useState, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { ThemeContext } from './ThemeContext';
import { AppThemeMode, ResolvedThemeMode, createAppTheme } from './theme';

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const getInitialTheme = (): AppThemeMode => {
    const savedTheme = localStorage.getItem('theme-preference');
    if (
      savedTheme === 'system' ||
      savedTheme === 'light' ||
      savedTheme === 'dark' ||
      savedTheme === 'tech'
    ) {
      return savedTheme as AppThemeMode;
    }
    return 'system';
  };

  const [mode, setMode] = useState<AppThemeMode>(getInitialTheme);
  const [systemMode, setSystemMode] = useState<ResolvedThemeMode>(() => {
    const prefersDarkMode =
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDarkMode ? 'dark' : 'light';
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const updateSystemMode = (matches: boolean) => {
      setSystemMode(matches ? 'dark' : 'light');
    };

    updateSystemMode(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      updateSystemMode(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const resolvedMode: ResolvedThemeMode = mode === 'system' ? systemMode : mode;

  const themeContextValue = useMemo(
    () => ({
      mode,
      resolvedMode,
      setMode: (newMode: AppThemeMode) => {
        setMode(newMode);
        localStorage.setItem('theme-preference', newMode);
      },
      toggleColorMode: () => {
        setMode(prevMode => {
          const newMode =
            prevMode === 'system'
              ? 'light'
              : prevMode === 'light'
                ? 'dark'
                : prevMode === 'dark'
                  ? 'tech'
                  : 'system';
          localStorage.setItem('theme-preference', newMode);
          return newMode;
        });
      },
    }),
    [mode, resolvedMode]
  );

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
